import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, Edit2, MoreVertical, Star, Trash2 } from 'lucide-react';
import { calculateOptimalPopupPosition, cn } from '../../../lib/utils';
import { FloatingDatePicker, UserInfoTooltip } from '../../shared';
import { formatDeadline } from '../../../lib/project-utils';
import type { TaskItem } from '../../../types/topic';
import { TaskModal } from './TaskModal';
import { useViewport } from '../../../contexts/ViewportContext';
import { logger } from '../../../utils/logger';
import { useTaskColumns } from '../../../hooks/queries/useTaskColumns';
import { extractBaseColor, getStatusColor } from '../../../utils/colorUtils';
import MobileTaskMenu from './mobile/MobileTaskMenu';

interface TaskCardProps {
  task: TaskItem & { _noDragHandling?: boolean };
  projectId?: string;
  itemToDelete: string | null;
  isFirst: boolean;
  isLast: boolean;
  index?: number;
  onDelete: (id: string) => void;
  onDeleteClick: (id: string) => void;
  onCancelDelete: () => void;
  onUpdateTask: (id: string, updates: Partial<TaskItem>) => void;
  onMoveTask?: (id: string, direction: 'up' | 'down' | 'top') => void;
  customDragHandlers?: boolean | Record<string, unknown>; // Flag to disable native drag handling or provide custom handlers
  onEditingStateChange?: (isEditing: boolean) => void; // To communicate editing state to parent
  hasCollaborators?: boolean; // Whether the project has collaborators
}

export function TaskCard({
  task,
  projectId,
  isFirst,
  isLast,
  onDelete,
  onUpdateTask,
  onMoveTask,
  customDragHandlers = false,
  hasCollaborators,
}: TaskCardProps) {
  const [showModal, setShowModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Add a reference to track if we're handling a date picker operation
  const datePickerActiveRef = useRef(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const deadlineButtonRef = useRef<HTMLButtonElement>(null);
  const { isMobile } = useViewport();

  // Handle click outside to close the menu and date picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Handle closing the menu
      if (
        menuOpen &&
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen, isDatePickerOpen]);

  // Handle clicking on the task card
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't handle the click if the click target is the menu button or its children
    if (buttonRef.current?.contains(e.target as Node)) {
      return;
    }

    // Both mobile and desktop: open the modal when clicking on the card
    setShowModal(true);
  };

  // Handle edit button click
  const handleEditClick = () => {
    // Both mobile and desktop: open the modal when clicking "Edit"
    setShowModal(true);
    setMenuOpen(false);
  };

  // Get column color information from React Query
  const { data: columns = [] } = useTaskColumns((task as any).topicId);

  // Convert column format to expected format for colorUtils
  const columnsForColorUtil = columns.map(col => {
    // Use our utility function to extract the base color
    const baseColor = extractBaseColor(col.color);

    // Use the extracted color or fall back to gray
    const colorName = baseColor || 'gray';

    return {
      status: col.status,
      color: colorName, // Just the base color name that colorUtils expects
    };
  });

  // Get the appropriate border color based on column color
  const borderColor = getStatusColor(
    task.status,
    columnsForColorUtil,
    'border',
    (task as any).isHighlighted
  );

  // We don't need to remove opacity here anymore as that's handled in each call site

  // Get the appropriate color class for tags based on task status
  const getTagColorClass = (status: string) => {
    return getStatusColor(status, columnsForColorUtil, 'tag');
  };

  // Get the background color for the user tooltip
  const bgColor = getStatusColor(task.status, columnsForColorUtil, 'bg');

  return (
    <div>
      {/* Task card */}
      <div
        ref={cardRef}
        className={cn(
          'group bg-white rounded-[5px] relative w-full p-4',
          'border-l-[5px] z-0',
          borderColor, // Border color is already handled to remove opacity
          !customDragHandlers && 'cursor-pointer', // Make it look clickable on all devices
          typeof customDragHandlers === 'object' && 'cursor-grab',
          'shadow hover:shadow-md transition-shadow'
        )}
        onClick={handleCardClick}
        {...(typeof customDragHandlers === 'object'
          ? Object.fromEntries(
              Object.entries(customDragHandlers).filter(([key]) => !['dragEnabled'].includes(key))
            )
          : {})}
        draggable={!customDragHandlers && !isMobile} // Only allow drag on desktop
        onDragStart={e => {
          if (customDragHandlers || isMobile) return;

          // Clear any previous data
          e.dataTransfer.clearData();

          // Set critical data for the drag operation
          e.dataTransfer.setData('taskId', task.id);
          e.dataTransfer.setData('sourceStatus', task.status);
          e.dataTransfer.effectAllowed = 'move';

          // Visual feedback for drag
          e.currentTarget.classList.add('opacity-50', 'scale-95');

          logger.component('Started dragging task', {
            taskId: task.id,
            taskTitle: task.title,
            sourceStatus: task.status,
          });
        }}
        onDragEnd={e => {
          if (customDragHandlers || isMobile) return;
          e.currentTarget.classList.remove('opacity-50', 'scale-95');
          logger.component('Ended dragging task', { taskId: task.id });
        }}
      >
        {/* Task dropdown menu button (top right) */}
        <div className="absolute right-2 top-2 z-50">
          <button
            ref={buttonRef}
            data-menu-button="true"
            onClick={e => {
              e.stopPropagation();

              if (!menuOpen && buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();

                // Use the shared calculateOptimalPopupPosition function
                const menuWidth = 190; // Approximate width of menu
                const menuHeight = 260; // Approximate height of menu with all options

                const position = calculateOptimalPopupPosition(
                  { top: rect.bottom, left: rect.left },
                  { width: menuWidth, height: menuHeight },
                  { x: 0, y: 5 }
                );

                // Use window.innerWidth as a function of position instead of direct reference
                const winWidth = document.documentElement.clientWidth;
                setMenuPosition({
                  top: position.top,
                  right: winWidth - position.left - menuWidth,
                });
              }

              setMenuOpen(!menuOpen);
            }}
            className={cn(
              'p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors more-vertical',
              !isMobile && 'opacity-0 group-hover:opacity-100' // Only hide on desktop, always show on mobile
            )}
          >
            <MoreVertical size={16} />
          </button>

          {/* Use MobileTaskMenu on mobile, otherwise use the original dropdown */}
          {menuOpen && isMobile ? (
            <MobileTaskMenu
              isOpen={menuOpen}
              onClose={() => setMenuOpen(false)}
              task={task}
              onEdit={handleEditClick}
              onDelete={onDelete}
              onUpdateTask={onUpdateTask}
              onMoveTask={onMoveTask}
              onOpenDatePicker={() => {
                datePickerActiveRef.current = true;
                setIsDatePickerOpen(true);
              }}
              isFirst={isFirst}
              isLast={isLast}
            />
          ) : (
            menuOpen &&
            createPortal(
              <>
                <div
                  className="fixed inset-0 z-[9998]"
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    setMenuOpen(false);
                  }}
                />
                <div
                  ref={menuRef}
                  data-task-menu="true"
                  className="fixed w-48 bg-white rounded-md shadow-lg border border-gray-200 overflow-visible z-[9999]"
                  style={{
                    top: `${menuPosition.top}px`,
                    right: `${menuPosition.right}px`,
                    maxHeight: 'calc(100vh - 20px)' /* Ensure it doesn't go off screen */,
                    overflowY: 'auto',
                  }}
                >
                  <div className="py-0">
                    {/* Show Edit option on all devices */}
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleEditClick();
                      }}
                      className="flex items-center w-full px-4 py-3 text-sm text-left hover:bg-gray-50"
                    >
                      <Edit2 size={14} className="mr-2 text-gray-600" />
                      <span>Edit</span>
                    </button>

                    {/* Deadline option */}
                    <button
                      ref={deadlineButtonRef}
                      onClick={e => {
                        e.stopPropagation();
                        setIsDatePickerOpen(true);
                      }}
                      className="flex items-center w-full px-4 py-3 text-sm text-left hover:bg-gray-50"
                    >
                      <Calendar size={14} className="mr-2 text-gray-600" />
                      <span>{task.deadline ? 'Edit Deadline' : 'Add Deadline'}</span>
                    </button>

                    {/* Highlight option */}
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        logger.component('Toggling highlight state', {
                          taskId: task.id,
                          currentHighlightState: (task as any).isHighlighted,
                          newHighlightState: !(task as any).isHighlighted,
                        });
                        // Handle both snake_case and camelCase versions based on data source
                        const isHighlighted = (task as any).isHighlighted !== undefined ? (task as any).isHighlighted : (task as any).is_highlighted;
                        onUpdateTask(task.id, { is_highlighted: !isHighlighted });
                        setMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-3 text-sm text-left hover:bg-gray-50"
                    >
                      <Star
                        size={14}
                        className={cn(
                          'mr-2',
                          (task as any).isHighlighted !== undefined ? (task as any).isHighlighted ? 'text-yellow-500' : 'text-gray-600' : (task as any).is_highlighted ? 'text-yellow-500' : 'text-gray-600'
                        )}
                      />
                      <span>{(task as any).isHighlighted !== undefined ? (task as any).isHighlighted ? 'Remove Highlight' : 'Highlight Task' : (task as any).is_highlighted ? 'Remove Highlight' : 'Highlight Task'}</span>
                    </button>

                    {/* Delete option */}
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        // Check if task.id is defined before calling onDelete
                        if (task && task.id) {
                          onDelete(task.id);
                        } else {
                          console.error('Cannot delete task: task ID is undefined');
                        }
                        setMenuOpen(false);
                      }}
                      className="flex items-center w-full px-4 py-3 text-sm text-left hover:bg-gray-50 text-red-600"
                    >
                      <Trash2 size={14} className="mr-2 text-red-600" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </>,
              document.body
            )
          )}
        </div>

        <div className="flex flex-col gap-2">
          {/* Title row */}
          <div className="w-full pr-7">
            {/* Add right padding to prevent overlap with menu button */}
            <h4
              className={cn('text-gray-900 break-words font-montserrat pt-[2px]', {
                'font-bold': (task as any).isHighlighted,
                'text-lg pt-0': isMobile,
              })}
            >
              {task.title}
            </h4>

            {task.deadline &&
              task.status !== 'done' &&
              (() => {
                const deadlineInfo = formatDeadline(task.deadline);
                if (!deadlineInfo) return null;

                // Get the task status and check if it's overdue or due today
                const isOverdue = deadlineInfo.isOverdue;
                const isToday = deadlineInfo.isToday;

                // Find the column for the current task status
                const taskColumn = columns.find(col => col.status === task.status);
                // Extract base color name for the column
                const columnBaseColor = taskColumn?.color
                  ? extractBaseColor(taskColumn.color)
                  : null;

                // We want a medium shade (-500) rather than dark (-800) to match column titles
                const getColorClass = (baseColor: string) => `text-${baseColor}-500`;

                // Select the appropriate color class
                let textColorClass;

                if (isOverdue) {
                  // Late tasks should be red
                  textColorClass = 'text-red-500';
                } else if (isToday) {
                  // Today tasks should be green
                  textColorClass = 'text-green-500';
                } else {
                  // All other tasks use column color
                  textColorClass = columnBaseColor
                    ? getColorClass(columnBaseColor)
                    : deadlineInfo.color;
                }

                return (
                  <div
                    className={`text-sm mt-2 ${textColorClass} ${isOverdue || isToday ? 'font-bold' : ''}`}
                  >
                    <div className="flex items-center gap-1">
                      <Calendar
                        size={isOverdue || isToday ? 14 : 12}
                        className={`
                          ${textColorClass}
                          ${isOverdue || isToday ? ' stroke-[2.5]' : ''}
                        `}
                      />
                      {isOverdue
                        ? deadlineInfo.lateByText
                        : deadlineInfo.isUpcoming
                          ? deadlineInfo.daysUntil === 1
                            ? 'Due tomorrow'
                            : `Due in ${deadlineInfo.daysUntil} days`
                          : deadlineInfo.formatted}
                    </div>
                  </div>
                );
              })()}
          </div>

          {/* Description row - full width (hidden for done tasks) - limited to 2 lines with ellipsis */}
          {task.description && task.status !== 'done' && (
            <p className="text-gray-400 mt-0 break-words text-sm w-full line-clamp-2">
              {task.description}
            </p>
          )}

          {/* Tags row - full width (hidden for done tasks) */}
          {task.tags && task.tags.length > 0 && task.status !== 'done' && (
            <div className="flex flex-wrap gap-1 mt-1 mb-2 w-full">
              {task.tags.map(tag => (
                <div
                  key={tag}
                  className={`${getTagColorClass(task.status)} rounded-full px-2 py-0.5 text-xs flex items-center`}
                >
                  <span>{tag}</span>
                </div>
              ))}
            </div>
          )}

          {/* Assignee indicator positioned in bottom right - always visible but subtle */}
          {(task as any).assignedTo && (
            <div
              className={cn(
                'absolute bottom-1 right-2 z-[100] transition-all duration-200',
                // On desktop: subtle gray by default, colored on hover
                !isMobile && 'opacity-40 grayscale group-hover:opacity-100 group-hover:grayscale-0'
              )}
              onClick={e => e.stopPropagation()} // Prevent card click handler from firing
              style={{ isolation: 'isolate' }}
            >
              <UserInfoTooltip
                userId={(task as any).assignedTo}
                fallbackName={(task as any).assigneeName}
                size="xs"
                position="top"
                iconType="circle"
                bgColor={bgColor}
              />
            </div>
          )}
        </div>
      </div>

      {/* Task edit modal for both mobile and desktop */}
      {showModal && (
        <TaskModal
          task={task}
          projectId={projectId}
          onClose={() => setShowModal(false)}
          onUpdateTask={onUpdateTask}
          hasCollaborators={hasCollaborators}
        />
      )}

      {/* Floating date picker for deadline */}
      {isDatePickerOpen &&
        createPortal(
          <FloatingDatePicker
            selectedDate={task.deadline ? new Date(task.deadline) : null}
            positionSource={
              isMobile
                ? { type: 'fullscreen' }
                : { type: 'buttonRef', ref: deadlineButtonRef, positionAtClick: true }
            }
            onDateChange={date => {
              // This is the critical step - update the task directly and synchronously
              try {
                onUpdateTask(task.id, {
                  deadline: date ? date.toISOString() : null,
                });
              } catch (err) {
                logger.error('Error updating task deadline', { taskId: task.id, error: err });
              }

              // Close the date picker
              setIsDatePickerOpen(false);

              // Close the menu if it was opened from the mobile menu
              if (datePickerActiveRef.current) {
                setMenuOpen(false);
                datePickerActiveRef.current = false;
              }
            }}
            onClose={() => {
              setIsDatePickerOpen(false);

              // Reset the reference when the date picker is closed
              if (datePickerActiveRef.current) {
                datePickerActiveRef.current = false;
              }
            }}
            onClear={() => {
              // Remove the deadline
              onUpdateTask(task.id, { deadline: null });
              setIsDatePickerOpen(false);
              setMenuOpen(false);

              // Reset the reference
              if (datePickerActiveRef.current) {
                datePickerActiveRef.current = false;
              }
            }}
            launchedFromMenu={true}
          />,
          document.body
        )}
    </div>
  );
}
